import { GridCellParams, GridRowModesModel, MuiEvent } from '@mui/x-data-grid';
import { GridRowModes } from '@mui/x-data-grid-premium';
import { MouseEvent, useCallback, useState } from 'react';
import type { GridApiRef } from '../DataGrid';
import { isColumnEditableForRow } from './cellEditability';

export default function useSingleClickEdit(
  apiRef: GridApiRef,
  opts: {
    /** Read-only switch. `false` ⇒ a click never opens row edit mode, independent of `params.isEditable`. Belt-and-braces with the grid-level `isCellEditable` gate: MUI derives `isEditable` from the column plus the grid prop, so a column marked `editable` on a grid whose `isCellEditable` has not re-rendered would still enter edit. Defaults to `true`. */
    canEdit?: boolean;
  } = {}
) {
  const { canEdit = true } = opts;
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const handleCellClick = useCallback(
    (params: GridCellParams, event: MuiEvent<MouseEvent>) => {
      if (!canEdit) {
        return;
      }

      if (!params.isEditable) {
        return;
      }

      if (!isColumnEditableForRow(params.colDef, params.row)) {
        event.preventDefault();
        event.stopPropagation();
        event.defaultMuiPrevented = true;
        return;
      }

      // Ignore portal
      const target = event.target instanceof Element ? event.target : null;
      if (target && !event.currentTarget.contains(target)) {
        return;
      }

      const api = apiRef.current;
      if (!api || api.getRowMode(params.id) === GridRowModes.Edit) {
        return;
      }

      // MUI's canonical recipes are not equivalent here: controlled-click caret
      // placement and add-row event ordering are observable product behavior.
      api.startRowEditMode({ id: params.id, fieldToFocus: params.field });

      // `fieldToFocus` opens the editor but does NOT make the cell the grid's focused one, so no cell reports `hasFocus`, `PopoverEditCell` renders its unfocused branch, and Tab is declined because `resolveActiveCell` finds no editing cell (TAS-3791).
      api.setCellFocus(params.id, params.field);

      // Set all rows to view mode except the clicked row
      setRowModesModel((prevModel) => {
        const newModel = { ...prevModel };
        Object.keys(newModel).forEach((id) => {
          if (id !== params.id.toString()) {
            newModel[id] = { mode: GridRowModes.View };
          }
        });

        // Set the clicked row to edit mode
        newModel[params.id] = { mode: GridRowModes.Edit };

        return newModel;
      });
    },
    [apiRef, canEdit]
  );

  const handleRowModesModelChange = useCallback((newModel: GridRowModesModel) => {
    setRowModesModel(newModel);
  }, []);

  return { rowModesModel, handleCellClick, handleRowModesModelChange, setRowModesModel };
}
